import {useCallback, useMemo} from "react";
import {Target} from "@/components/TargetSelector";
import {UpdateGatewayDataQuery, useUpdateGatewayDataQuery} from "./queries.generated";
import {useGetCallbackDataQuery} from "../queries.generated";
import {gatewayCallbackDataToTargets} from "../common";
import {UpdateGatewayData} from "@/api";

export type DataSource = {
    gateway: UpdateGatewayData;
    domainData: UpdateGatewayDataQuery["gateways"]["items"][number]["number"]["domain"];
    callbackTargets: Target[];
    loading: boolean;
    refetch: () => Promise<UpdateGatewayData>;
};

const separateNumberField = (
    gateway: UpdateGatewayDataQuery["gateways"]["items"][number],
): [UpdateGatewayData, UpdateGatewayDataQuery["gateways"]["items"][number]["number"]] => {
    const {number, numberId: _, ...gatewayData} = gateway;
    return [gatewayData, number];
};

export const useDataSource = (gatewayId: number, domainId: number): DataSource => {
    const gatewayQuery = useUpdateGatewayDataQuery({
        variables: {id: gatewayId},
    });

    const callbackQuery = useGetCallbackDataQuery({
        variables: {
            domainId,
        },
    });

    const callbackTargets = useMemo(
        () => (callbackQuery.data ? gatewayCallbackDataToTargets(callbackQuery.data) : []),
        [callbackQuery.data],
    );

    const refetch = useCallback(async () => {
        const result = await gatewayQuery.refetch();
        const [gateway] = separateNumberField(result.data.gateways.items[0]);
        return gateway;
    }, [gatewayQuery]);

    if (gatewayQuery.loading || callbackQuery.loading)
        return {
            gateway: undefined,
            domainData: undefined,
            callbackTargets: [],
            loading: true,
            refetch,
        };

    const gatewayData = gatewayQuery.data?.gateways.items[0];
    const [gateway, number] = separateNumberField(gatewayData);

    return {
        gateway,
        domainData: number.domain,
        callbackTargets,
        loading: false,
        refetch,
    };
};
